// ===========================================================================
// KpiTiles — header de 4 tiles que reflejan el estado filtrado.
//
//   [Total Members] [Networks donut] [Time Since donut] [PCP Count]
//
// Los donuts son interactivos: click en un segmento aplica/limpia el filtro
// correspondiente (networkName / timeBucket). Sin filtros muestran los totales
// del módulo; con filtros, los agregados del subconjunto.
// ===========================================================================
import React, { useMemo } from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import DistributionDonut from "./DistributionDonut";
import { formatNumber } from "../../utils/format";
import { TIME_BUCKETS, timeBucketColor, networkColor } from "../../common/constants";

function NumberTile({ label, value, loading }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={120} height={56} />
      ) : (
        <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(value)}
        </Typography>
      )}
    </Paper>
  );
}

export default function KpiTiles({
  kpis,
  loading,
  activeNetwork,
  activeTimeBucket,
  onSelectNetwork,
  onSelectTimeBucket,
}) {
  // Networks: el backend ya los entrega de mayor a menor. value = networkName.
  const networkData = useMemo(
    () =>
      (kpis?.networks || []).map((n, i) => ({
        label: n.networkName,
        value: n.networkName,
        count: n.count,
        color: networkColor(i),
      })),
    [kpis]
  );

  // Time buckets: forzamos el orden lógico de severidad (no el del backend) y el
  // color de severidad por bucket. value = timeBucket.
  const timeData = useMemo(() => {
    const byBucket = new Map((kpis?.timeBuckets || []).map((b) => [b.timeBucket, b.count]));
    return TIME_BUCKETS.filter((b) => byBucket.has(b)).map((b) => ({
      label: b,
      value: b,
      count: byBucket.get(b),
      color: timeBucketColor(b),
    }));
  }, [kpis]);

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1.4fr 1.4fr 1fr" },
        mb: 2,
      }}
    >
      <NumberTile label="Total Members" value={kpis?.totals?.totalMembers} loading={loading} />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
        {loading ? (
          <Skeleton variant="rounded" height={110} />
        ) : (
          <DistributionDonut
            title="Networks"
            ariaLabel="Distribution by network"
            data={networkData}
            activeValue={activeNetwork}
            onSelect={onSelectNetwork}
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
        {loading ? (
          <Skeleton variant="rounded" height={110} />
        ) : (
          <DistributionDonut
            title="Time Since Last Visit"
            ariaLabel="Distribution by time since last visit"
            data={timeData}
            activeValue={activeTimeBucket}
            onSelect={onSelectTimeBucket}
          />
        )}
      </Paper>

      <NumberTile label="PCP Count" value={kpis?.totals?.pcpCount} loading={loading} />
    </Box>
  );
}
